import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';

import { NavigationModule } from '@progress/kendo-angular-navigation';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { BreakpointService } from '../services/breakpoint.service';
import { MsalModule, MsalService } from '@azure/msal-angular';
import { NavItemLabelIds, RoutePath } from '../enums/api.enum';
import { SessionStatus } from '../enums/session-status.enum';
import { UiLayoutService } from './services/ui-layout.service';
import { ProfileService } from '../services/profile.service';
import { ApiService } from '../services/api.service';
import { AuthService } from '../services/auth.service';
import { catchError, first, of, Subscription, switchMap, tap } from 'rxjs';
import { ChatSessionService } from '../pages/ai-chat/services/chat-session.service';
import { ChatMessageService } from '../pages/ai-chat/services/chat-message.service';
import { INavMenuItem } from '../interfaces/i-nav-menu-item';
import { InitializationService } from '../services/initialization.service';
import { AlertService } from '../shared/alert/alert.service';
import { AlertEnum } from '../shared/alert/alert.enum';
import { AiChatService } from '../pages/ai-chat/services/ai-chat.service';
import {
  ATTACHED_DOCUMENT_MAX_FILE_SIZE,
  MAX_DOCUMENTS_ALLOWED,
  MAX_MESSAGES_TO_USE,
  MAX_TOKENS
} from "../constants/api.constant";
import { GsmComponent } from "../pages/general-system-messaging/gsm/gsm.component";
import { NotificationService } from "../services/notification.service";
import { UtilitiesService } from "../services/utilities.service";
import { RouteGuardService } from "../services/route-guard.service";
import { LeftSidebar } from "./left-sidebar/left-sidebar";
import { RightSidebar } from "./right-sidebar/right-sidebar";
import { DrawerContainer } from "../shared/drawer-container/drawer-container";
import { RightSidebarState } from "./right-sidebar/right-sidebar-state";
import { LeftSidebarState } from "./left-sidebar/left-sidebar-state";
import { filter } from "rxjs/operators";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: 'ui-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    NavigationModule,
    MsalModule,
    GsmComponent,
    LeftSidebar,
    RightSidebar,
    DrawerContainer,
  ],
  templateUrl: './ui-layout.component.html',
  styleUrl: './ui-layout.component.scss',
  host: {
    // Manage both left and right widths dynamically
    // These bindings update CSS variables in real-time as signals change
    '[style.--left-sidebar-width.px]': 'leftSidebarWidth()',
    '[style.--right-sidebar-width.px]': 'rightSidebarWidth()',
    '[class.show-right]': 'shouldShowRightColumn()',
    '[class.is-resizing]': 'isResizing()'
  }
})
export class UiLayoutComponent implements OnInit {
  protected readonly rightSidebarState = inject(RightSidebarState);
  protected readonly leftSidebarState = inject(LeftSidebarState);
  /**
   * Current width of the left sidebar in pixels.
   * Bound to the CSS variable `--left-sidebar-width`.
   */
  leftSidebarWidth = signal(250); // initial left width in pixels

  /**
   * Current width of the right sidebar in pixels.
   * Bound to the CSS variable `--right-sidebar-width`.
   */
  rightSidebarWidth = signal(250); // initial right width in pixels

  /**
   * Controls whether the grid allocates space for the right sidebar column.
   * This aligns grid behavior with `RightSidebarState.width()` (e.g. Hidden \=\> 0px \=\> no column).
   */
  protected readonly shouldShowRightColumn = computed<boolean>(() => {
    return this.rightSidebarState.isVisible() && this.rightSidebarState.width() > 0;
  });

  uiLayoutService = inject(UiLayoutService);

  #isResizing = signal(false);
  protected readonly isResizing = this.#isResizing.asReadonly();

  readonly #activePointerId = signal<number | null>(null);

  protected readonly showRightSplitter = computed<boolean>(() => {
    return (
      this.rightSidebarState.isVisible() &&
      this.rightSidebarState.isOpen() &&
      !this.#breakpointService.isMobile()
    );
  });

  #breakpointService = inject(BreakpointService);
  #router = inject(Router);
  #msalService = inject(MsalService);
  #apiService = inject(ApiService);
  #authService = inject(AuthService);
  #profileService = inject(ProfileService);
  #chatSessionService = inject(ChatSessionService);
  #chatMessageService = inject(ChatMessageService);
  initializationService = inject(InitializationService);
  #alertService = inject(AlertService);
  #aiChatService = inject(AiChatService);
  #notificationService = inject(NotificationService);
  #utilitiesService = inject(UtilitiesService);
  #routeGuardService = inject(RouteGuardService);

  constructor() {
    // Close right sidebar on route navigation
    this.#router.events.pipe(
      // Check when a user navigates to a new route
      filter(event => event instanceof NavigationStart),
      // Automatically unsubscribes when UiLayoutComponent is destroyed
      takeUntilDestroyed()
    ).subscribe(() => this.rightSidebarState.close());

    effect(() => {
      this.leftSidebarWidth.set(this.leftSidebarState.width());
      this.rightSidebarWidth.set(this.rightSidebarState.width());
    });

    effect((onCleanup) => {
      if (!this.#isResizing()) {
        return;
      }

      const onMove = (event: PointerEvent): void => {
        if (!this.#isResizing()) {
          return;
        }
        const activeId = this.#activePointerId();
        if (activeId !== null && event.pointerId !== activeId) {
          return;
        }

        const widthPx = Math.round(window.innerWidth - event.clientX);
        this.rightSidebarState.setWidthPx(widthPx);
      };

      const onEnd = (event: PointerEvent): void => {
        const activeId = this.#activePointerId();
        if (activeId !== null && event.pointerId !== activeId) {
          return;
        }
        this.onSplitterResizeEnd();
      };

      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerup', onEnd, { passive: true });
      window.addEventListener('pointercancel', onEnd, { passive: true });

      onCleanup(() => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onEnd);
        window.removeEventListener('pointercancel', onEnd);
      });
    });

    effect((onCleanup) => {
      if (!this.#isResizing()) {
        return;
      }

      const previousUserSelect = document.body.style.userSelect;
      const previousCursor = document.body.style.cursor;

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      onCleanup(() => {
        document.body.style.userSelect = previousUserSelect;
        document.body.style.cursor = previousCursor;
      });
    });

    effect((onCleanup) => {
      // Get the access token.  If there is one, make the following API calls to configure the application and user.
      const token = this.#authService.accessToken();

      // Variable to hold the main API subscription
      let subscription: Subscription | undefined;

      if (token) {
        subscription = this.#apiService.getInitialize().pipe(
          switchMap(rsp => {
            if (rsp) {
              return this.#apiService.validateToken();
            } else {
              return of(null);
            }
          }),
          tap(data => {
            if (data) {
              const currentProfile = this.#profileService.userProfile();
              if (currentProfile && currentProfile.profile) {
                this.#profileService.userProfile.set({
                  ...currentProfile,
                  config: {
                    canCreateAgents: data.canCreateAgents ?? false,
                    canManageAgents: data.canManageAgents ?? false,
                    canManageProfiles: data.canManageProfiles ?? false,
                    isAdmin: data.isAdmin ?? false,
                    canCreateWorkflows: data.canCreateWorkflows ?? false,
                    canManageWorkflows: data.canManageWorkflows ?? false,
                    canUseWorkflows: data.canUseWorkflows ?? false,
                    defaultSession: data.defaultSession,
                    maxDocumentSize: data.maxDocumentSize ?? ATTACHED_DOCUMENT_MAX_FILE_SIZE,
                    maxDocuments: data.maxDocuments ?? MAX_DOCUMENTS_ALLOWED,
                    maxTokens: data.maxTokens ?? MAX_TOKENS,
                    maximumMessagesToUse: data.maximumMessagesToUse ?? MAX_MESSAGES_TO_USE,
                  }
                });
              }
            }
          }),
          switchMap(data => {
            if (data) {
              return this.#apiService.getSessions();
            } else {
              return of(null);
            }
          }),
          tap(sessions => {
            const currentSessions = this.#chatSessionService.chatSessions();

            if (!currentSessions) {
              if (sessions) {
                // Reset transient statuses (PROCESSING/PAUSED) to DEFAULT after page refresh
                // These statuses represent active states that don't persist across page refreshes
                const normalizedSessions = sessions.map(session => {
                  if (session.lastStatus === SessionStatus.PROCESSING || session.lastStatus === SessionStatus.PAUSED) {
                    return { ...session, lastStatus: SessionStatus.DEFAULT };
                  }
                  return session;
                });

                this.#chatSessionService.chatSessions.set(normalizedSessions);
                const sessionId = this.#chatSessionService.selectedSessionId();

                if (sessionId) {
                  // SessionId exist - This means it is in the URL
                  this.#chatMessageService.messages.set(this.#chatSessionService.selectedSession()?.messages);

                  // checks for if the currently selected session has any messages stuck in progress.
                  // if so, we set those to a default error message.
                  // this is to fix an issue with the chat not updating the message status when the user navigates away from the chat
                  // and the Cancel button appearing in the chat bubble. when click, will remove the wrong message.
                  this.#aiChatService.checkForStuckInProgressMessages()
                } else {
                  if (sessions.length > 0) {
                    // Set the sessionId from the response from "sessions/my".  Default to the first in the array.
                    this.#chatSessionService.selectedSessionId.set(sessions[0].id);
                    this.#chatMessageService.messages.set(sessions[0].messages);
                  }
                }

                // Manage the nav menu items.  Add the sessions to the menu.
                let sessionItems = this.uiLayoutService.createNavItemsFromChatSessions(this.#chatSessionService.chatSessions()!);

                this.uiLayoutService.updateNavItems(sessionItems);

                this.#updateNavMenu();

                // Signal that initialization is complete
                if (!this.initializationService.initializationComplete()) {
                  this.initializationService.initializationComplete.set(true);
                }

                this.#routeTo();
              }
            }

            this.#utilitiesService.setDefaultLocationFrom()
          }),
          catchError(error => {
            this.#alertService.showAlert({
              message: `Failed to load application.`,
              type: AlertEnum.error,
            });
            console.error('Error occurred:', error);
            return of(null);
          })
        ).subscribe();
        this.#notificationService.refresh();
      }
      // Register the cleanup function
      onCleanup(() => {
        if (subscription) {
          subscription.unsubscribe();
        }
      });
    });
  }

  ngOnInit(): void {
    /*
    This is responsible for handling the authentication result after a redirect-based authentication flow.
    EXPLANATION
    1. handleRedirectObservable:
        - This method is part of the MsalService provided by the @azure/msal-angular library.
        - It returns an observable that emits the authentication result after the user is redirected back to the application following an interactive authentication flow (e.g., after logging in via a redirect).
    2. Subscription to the Observable:
        - The subscribe method is used to listen for the authentication result emitted by the handleRedirectObservable.
        - The subscription has two callbacks: next and error.
    3. next Callback:
        - This callback is invoked when the observable emits a new value, which is the authentication result.
        - The result parameter contains the authentication result, which includes information such as the access token and account details.
        - If result is truthy (i.e., the authentication was successful), the acquireToken method of the AuthService is called.
        - The acquireToken method is responsible for acquiring an access token, either silently or via a redirect if necessary.
    4. error Callback:
        - This callback is invoked if an error occurs while handling the redirect.
        - The error is logged to the console using console.error.
    PURPOSE
    The primary purpose of this code snippet is to handle the authentication result after the user is redirected back to the application following an interactive authentication flow.
    It ensures that the application can acquire an access token and proceed with authenticated operations.
    By handling the redirect and acquiring the access token, the application ensures that the user is authenticated and can access protected resources.
    FLOW
    1. User Initiates Login: The user initiates a login process that involves a redirect to the Microsoft login page.
    2. User Authenticates: The user completes the authentication on the Microsoft login page.
    3. Redirect Back to Application: After successful authentication, the user is redirected back to the application with the authentication result.
    4. Handling the Redirect:
        - The handleRedirectObservable method captures the authentication result.
        - The next callback processes the result and calls acquireToken to obtain an access token.
    */
    this.#msalService.handleRedirectObservable().subscribe({
      next: (result) => {
        if (result) {
          this.#authService.acquireToken();
        }
      },
      error: (error) => {
        console.error(error);
      }
    });

    /*
    Checks if there are any authenticated user accounts available in the MSAL (Microsoft Authentication Library) instance, and if so, it proceeds to acquire an access token for the authenticated user.
    */
    if (this.#msalService.instance.getAllAccounts().length > 0) {
      this.#authService.acquireToken();
    } else {
      this.#router.navigate([ `/${RoutePath.SignIn}` ]);
    }

    // Periodically check and refresh the token
    setInterval(() => {
      this.#authService.checkAndRefreshToken();
    }, 5 * 60 * 1000); // Check every 5 minutes
  }

  /**
   * Handles the resizeable event on the right sidebar splitter.
   * Initiates resizing by capturing the pointer, setting the active pointer ID,
   * and updating the resizing state. Sets the sidebar width based on the pointer position.
   *
   * @param {PointerEvent} event - The pointer event triggered by user interaction.
   */
  protected onSplitterPointerDown(event: PointerEvent): void {
    if (!this.showRightSplitter()) {
      return;
    }

    // Only primary button (avoid right click / extra buttons)
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);

    this.#activePointerId.set(event.pointerId);
    this.#isResizing.set(true);

    const widthPx = Math.round(window.innerWidth - event.clientX);
    this.rightSidebarState.setWidthPx(widthPx);
  }

  protected onSplitterResizeEnd(): void {
    if (!this.#isResizing()) {
      return;
    }
    this.#isResizing.set(false);
    this.#activePointerId.set(null);
  }

  /**
   * Sets the route based on the current route.
   * This only runs after the initial load of the page due to the default navigation items which will direct the user the /session.
   */
  #routeTo() {
    // this checks to see if we have locationFrom set. if we do, update it. else, we haven't set it to the default value of a session id yet.
    if (this.#utilitiesService.locationFrom != '') {
      this.#utilitiesService.locationFrom = this.#router.url;
    }

    // Capture the initial URL and whether this is the initial page load before routeGuard() consumes them.
    // isInitialPageLoad starts as true and is flipped to false the moment routeGuard() runs. This constant captures its
    // value before routeGuard() is called, so it records whether this particular call to #routeTo() is the very first
    // load of the app. On every subsequent navigation it will be false.
    const wasInitialPageLoad = this.#routeGuardService.isInitialPageLoad;

    // loadedUrl holds the path the browser had when the app first bootstrapped — e.g. edit-workflow/abc-123/details.
    // After routeGuard() runs it has already acted on this value, but the value itself doesn't get cleared.
    // This constant captures a snapshot of it before routeGuard() runs so the logic below can inspect what the original
    // URL actually was.
    const initialLoadedUrl = this.#routeGuardService.loadedUrl;

    // Splits loadedUrl and takes only the first path segment — the route name without any ID or step. For example:
    const initialTopSegment = initialLoadedUrl.split('/')[0];

    // Determine if the initial URL was a session route or root (empty).
    // For these cases the legacy nav-item fallback navigation must still run.
    //
    // This is true for exactly two cases:
    //   1. User opened the app with no path (e.g. https://app.example.com/)
    //      → initialTopSegment is '' (empty string, falsy)
    //   2. User opened a session URL (e.g. /session/xyz)
    //      → initialTopSegment is 'session'
    //
    // For everything else — /edit-workflow/..., /marketplace, /edit-agent/... — it is false.
    const isSessionOrRootUrl = !initialTopSegment || initialTopSegment === RoutePath.Session;

    this.#routeGuardService.routeGuard();

    // On the initial page load for a deep-linked non-session route (e.g. /edit-workflow/:id
    // accessed from an email or bookmark), routeGuard() has already performed the correct
    // navigation. Do NOT override it by re-routing to the currently selected nav item.
    if (wasInitialPageLoad && !isSessionOrRootUrl) {
      return;
    }

    const selectedNavItem = this.uiLayoutService.navItems().find(item => item.selected === true);
    if (selectedNavItem && selectedNavItem.parentId !== NavItemLabelIds.Help) {
      this.#router.navigateByUrl(selectedNavItem?.path!);
    }
  }

  /**
   * Update the nav menu so that the given item will be selected.  If there is no given item, then use the URL.
   */
  #updateNavMenu(item?: INavMenuItem) {
    let selectedNavItem = item;

    if (!item) {
      const urlSegment = this.#router.url.split('/');
      // If the url is a session, get the id in the URL and set the nav item that is equal to it to selected true.
      if (urlSegment[1] === RoutePath.Session) {
        if (urlSegment.length === 3) {
          selectedNavItem = this.uiLayoutService.navItems().find(item => item.id === urlSegment[2]);
        } else {
          const sessionId = this.#chatSessionService.selectedSession()?.id;
          selectedNavItem = sessionId
            ? this.uiLayoutService.navItems().find(navItem => navItem.id === sessionId)
            : undefined;
        }
      } else {
        selectedNavItem = this.uiLayoutService.navItems().find(item => item.path === urlSegment[1]);
      }
    }

    this.uiLayoutService.setSelectedNavItem(selectedNavItem?.id!);
  }
}
